/**
 * Staging + dirty-state helpers for the campaign Conversions tab's edit modal (#6479).
 *
 * Row edits stage into the campaign draft (`CampaignDraft.conversionActions`) and persist
 * via the page's Save Changes button, like excluded locations. Every field the edit modal
 * can change MUST be compared in `isConversionEntryEdited` — a field missing from the
 * comparison means Save Changes never arms for that edit and it is silently dropped on
 * navigation (#6472 lesson).
 *
 * Types are structural subsets of `CampaignConversionLink` so this module stays
 * importable from tests without pulling in the API layer.
 */

/** The editable subset of a linked conversion action, staged per row in the draft. */
export interface CampaignConversionEdit {
  conversionActionId: number;
  name: string;
  funnelStage: string | null;
  defaultValue: number | null;
  primaryForGoal: boolean | null;
}

/** Structural subset of a GET /campaigns/{id}/conversion-actions row. */
export interface ConversionLinkLike {
  conversion_action_id: number;
  conversion_action: {
    name: string;
    funnel_stage: string | null;
    default_value: number | null;
    primary_for_goal: boolean | null;
  } | null;
}

/** Only changed fields, in the PATCH endpoint's snake_case shape. */
export interface ConversionEditDiff {
  conversionActionId: number;
  update: {
    name?: string;
    funnel_stage?: string;
    default_value?: number;
    primary_for_goal?: boolean;
  };
}

/** Map the campaign conversion-links query rows to draft entries (rows without a
 * registry action attached are not editable and are skipped). */
export function toConversionDraftEntries(links: ConversionLinkLike[]): CampaignConversionEdit[] {
  return links
    .filter((link) => link.conversion_action !== null)
    .map((link) => ({
      conversionActionId: link.conversion_action_id,
      name: link.conversion_action!.name,
      funnelStage: link.conversion_action!.funnel_stage,
      defaultValue: link.conversion_action!.default_value,
      primaryForGoal: link.conversion_action!.primary_for_goal,
    }));
}

/** Whether one row's staged entry differs from its baseline — EVERY editable field. */
export function isConversionEntryEdited(
  entry: CampaignConversionEdit,
  original: CampaignConversionEdit | undefined
): boolean {
  if (!original) return true;
  return (
    entry.name !== original.name ||
    entry.funnelStage !== original.funnelStage ||
    entry.defaultValue !== original.defaultValue ||
    entry.primaryForGoal !== original.primaryForGoal
  );
}

/** Whether any staged conversion edit differs from the baseline. undefined = not hydrated = clean. */
export function isConversionDraftDirty(
  draft: CampaignConversionEdit[] | undefined,
  original: CampaignConversionEdit[] | undefined
): boolean {
  if (draft === undefined || original === undefined) return false;
  if (draft.length !== original.length) return true;
  const baseline = new Map(original.map((entry) => [entry.conversionActionId, entry]));
  return draft.some((entry) => isConversionEntryEdited(entry, baseline.get(entry.conversionActionId)));
}

/** Per-row changed fields for Save Changes — one PATCH per edited row, changed fields only. */
export function diffConversionEdits(
  draft: CampaignConversionEdit[] | undefined,
  original: CampaignConversionEdit[] | undefined
): ConversionEditDiff[] {
  if (draft === undefined || original === undefined) return [];
  const baseline = new Map(original.map((entry) => [entry.conversionActionId, entry]));
  const diffs: ConversionEditDiff[] = [];
  for (const entry of draft) {
    const base = baseline.get(entry.conversionActionId);
    if (!base) continue; // link appeared after staging — nothing was edited for it
    const update: ConversionEditDiff['update'] = {};
    if (entry.name !== base.name) update.name = entry.name;
    if (entry.funnelStage !== base.funnelStage && entry.funnelStage !== null) update.funnel_stage = entry.funnelStage;
    if (entry.defaultValue !== base.defaultValue && entry.defaultValue !== null) {
      update.default_value = entry.defaultValue;
    }
    if (entry.primaryForGoal !== base.primaryForGoal && entry.primaryForGoal !== null) {
      update.primary_for_goal = entry.primaryForGoal;
    }
    if (Object.keys(update).length > 0) diffs.push({ conversionActionId: entry.conversionActionId, update });
  }
  return diffs;
}

/**
 * Re-seed the draft baseline from the server's current link list while carrying staged
 * edits forward. Needed because Add/Delete on the Conversions tab mutate immediately while
 * row edits stage: the list can move under pending edits (a row linked or unlinked), and a
 * plain "only re-seed while untouched" guard (the excluded-locations pattern) would either
 * clobber the pending edits or leave removed rows in the draft. Removed rows drop their
 * staged edits; new rows join unedited; edited surviving rows keep their staged values.
 */
export function mergeConversionDraft(
  server: CampaignConversionEdit[],
  prevDraft: CampaignConversionEdit[] | undefined,
  prevOriginal: CampaignConversionEdit[] | undefined
): { draft: CampaignConversionEdit[]; original: CampaignConversionEdit[] } {
  const prevBaseline = new Map((prevOriginal ?? []).map((entry) => [entry.conversionActionId, entry]));
  const prevStaged = new Map((prevDraft ?? []).map((entry) => [entry.conversionActionId, entry]));
  const draft = server.map((entry) => {
    const staged = prevStaged.get(entry.conversionActionId);
    if (staged && isConversionEntryEdited(staged, prevBaseline.get(entry.conversionActionId))) return staged;
    return entry;
  });
  return { draft, original: server };
}

/** Per-field tell-on-click validation for the edit modal. Returns an empty object when valid. */
export function validateConversionEditInput(input: {
  name: string;
  valueText: string;
  baselineHadValue: boolean;
}): { name?: string; value?: string } {
  const errors: { name?: string; value?: string } = {};
  if (!input.name.trim()) errors.name = 'Required';
  const trimmedValue = input.valueText.trim();
  if (trimmedValue === '') {
    // Google Ads cannot unset a default value once one exists — 0 is the way to clear it.
    if (input.baselineHadValue) errors.value = 'Required';
  } else {
    const parsed = Number(trimmedValue);
    if (!Number.isFinite(parsed) || parsed < 0) errors.value = 'Enter a valid amount';
  }
  return errors;
}
